import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { useMemo } from "react";

import usePolledQuery from "apollo/usePolledQuery";
import { PollingIntervalGroups } from "apollo/constants";
import usePaginationCursorReset from "components/Table/Pagination/usePaginationCursorReset";
import {
  INTENT_PROJECT_RESOURCES_LIMIT,
  INTENT_PROJECT_RESOURCES_LIMIT_STORAGE_KEY,
} from "views/Account/IntentProject/Resources/Table/constants";

import { SEARCH_INTENT_RESOURCES } from "./gql";
import useIntentProjectResourcesStore from "./store";

const useSearchIntentResources = (intentProjectId: string) => {
  const {
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  } = useIntentProjectResourcesStore();
  const [storedRowsPerPage] = useLocalStorage<number>(INTENT_PROJECT_RESOURCES_LIMIT_STORAGE_KEY);

  const first = storedRowsPerPage || INTENT_PROJECT_RESOURCES_LIMIT;
  const paginationResetKey = intentProjectId;

  const { cursor, requestedPage } = usePaginationCursorReset({
    resetKey: paginationResetKey,
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  });

  const { data, error, previousData, networkStatus } = usePolledQuery(SEARCH_INTENT_RESOURCES, {
    variables: {
      intentProjectId,
      input: {
        fullTextSearch: "",
        first,
        after: cursor || null,
        orderBy: [],
        predicates: [],
        requestedPage,
      },
    },
    pollingGroup: PollingIntervalGroups.SingleEntities,
  });

  const resources = useMemo(() => {
    const edges =
      data?.intentProject?.searchIntentResources?.edges ??
      previousData?.intentProject?.searchIntentResources?.edges ??
      [];

    return edges.map((edge) => edge.node);
  }, [data, previousData]);

  const initialLoading = resources.length === 0 && networkStatus === NetworkStatus.loading;

  const isEmpty = Boolean(data) && resources.length === 0 && !initialLoading;

  return {
    resources,
    initialLoading,
    isEmpty,
    error,
    startCursor: data?.intentProject?.searchIntentResources?.pageInfo.startCursor,
    endCursor: data?.intentProject?.searchIntentResources?.pageInfo.endCursor,
    paginationResetKey,
  };
};

export default useSearchIntentResources;
